/**
 * TPL Scroll Bus — one scroll/resize listener and one rAF for the whole page.
 *
 * Why: every scroll-painting module used to own a listener and a rAF. Module A
 * wrote styles in its frame, module B then measured in its own frame, and the
 * browser had to recompute layout in between — the layout thrashing MotionScore
 * flagged (2026-10-03). A bus fixes it structurally: every painter's READ runs
 * first, every painter's WRITE runs after, inside a single frame.
 *
 * Register a painter with two pure-ish halves:
 *   TPLScrollBus.add({
 *     read:  function () { return { ...measurements... }; },  // no style writes
 *     write: function (state) { ...apply styles...  }         // no measurements
 *   });
 *
 * `read` may be omitted for write-only painters. A painter that throws is
 * dropped from that frame, never from the bus, so one bad module cannot stop
 * the others. Consumers should fall back to their own listener when
 * window.TPLScrollBus is undefined (this file failing to load must not kill
 * scroll effects).
 */
(function () {
  'use strict';

  if (window.TPLScrollBus) return;

  var painters = [];
  var ticking = false;
  var listening = false;

  function flush() {
    ticking = false;
    var n = painters.length;
    var states = new Array(n);
    /* READ pass — measurements only */
    for (var i = 0; i < n; i++) {
      var p = painters[i];
      try { states[i] = p.read ? p.read() : null; } catch (e) { states[i] = undefined; }
    }
    /* WRITE pass — style changes only */
    for (var j = 0; j < n; j++) {
      var q = painters[j];
      if (states[j] === undefined && q.read) continue;
      try { if (q.write) q.write(states[j]); } catch (e) { /* keep the other painters alive */ }
    }
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(flush);
  }

  function listen() {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
  }

  window.TPLScrollBus = {
    add: function (painter) {
      if (!painter) return painter;
      painters.push(painter);
      listen();
      schedule();
      return painter;
    },
    remove: function (painter) {
      var i = painters.indexOf(painter);
      if (i >= 0) painters.splice(i, 1);
    },
    schedule: schedule,
    /* Run the read/write passes NOW, inside the caller's own frame. Smooth scroll owns a
       continuous rAF while momentum runs; if it merely called schedule() every painter would
       land one frame behind the transform and visibly lag the page. Only the smooth-scroll
       driver should use this: everything event-driven wants schedule(). */
    flush: function () { ticking = false; flush(); },
    count: function () { return painters.length; }
  };
})();
